// 📄 pages/Proyectos/components/IncidenteDetailModal.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Download, FileText, Send, RefreshCw, Save, Pencil, RotateCcw, CalendarCheck, AlertTriangle } from 'lucide-react';
import {
  Incidente,
  IncidenteEstado,
  IncidentePrioridad,
  IncidenteFase,
  IncidenteAmbiente,
  IncidenteCategoriaSR,
  IncidentePlataforma,
  IncidenteCanal,
  IncidenteDatosEditables,
  DetalleResolucion,
  DetalleCierre,
  KnownErrorRef,
  ESTADOS,
  PRIORIDADES,
  PLATAFORMAS,
  TIPOS_AJUSTE,
  MOTIVOS_CIERRE,
  FASES,
  FASE_LABELS,
  AMBIENTES,
  CATEGORIAS_SR,
  CANALES_INTEGRACION,
  updateIncidenteEstado,
  updateIncidentePrioridad,
  updateDetalleResolucion,
  updateDetalleCierre,
  updateIncidenteDatos,
  updateCausaRaiz,
  updateSolucionAplicada,
  updateNotaSapManual,
  linkErrorConocido,
  unlinkErrorConocido,
  addBitacoraEntry
} from '../../../services/incidenteService.ts';
import { KnownError, getAllKnownErrors, isKnownErrorActivo } from '../../../services/knownErrorsService.ts';
import { ProjectMember } from '../../../services/projectService.ts';
import { useAuth } from '../../../context/AuthContext.tsx';
import { useProject } from '../../../context/ProjectContext.tsx';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { PRIORIDAD_STYLES, ESTADO_STYLES } from './incidenteStyles.ts';
import { getChecklistStepLabel } from '../../../data/commerceChecklistCatalog.ts';
import ChecklistStepPicker from './ChecklistStepPicker.tsx';
import IncidenteErrorConocidoLink from './IncidenteErrorConocidoLink.tsx';
import { formatFechaConTiempoRelativo } from '../../../utils/formatDate.ts';

interface IncidenteDetailModalProps {
  projectId: string;
  incidente: Incidente;
  miembros: ProjectMember[];
  solucionesActivas: string[];
  // Proyecto sin SAP Ariba: el campo es "Área" (opcional) en vez de "Solución relacionada".
  modoArea?: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export default function IncidenteDetailModal({ projectId, incidente, miembros, solucionesActivas, modoArea = false, onClose, onUpdated }: IncidenteDetailModalProps) {
  const { user, profile } = useAuth();
  const { esCliente, esAdminObservador } = useProject();
  const [estado, setEstado] = useState<IncidenteEstado>(incidente.estado);
  const [prioridad, setPrioridad] = useState<IncidentePrioridad>(incidente.prioridad);
  const [bitacora, setBitacora] = useState(incidente.bitacora || []);
  const [comentario, setComentario] = useState('');
  const [savingComentario, setSavingComentario] = useState(false);
  const [savingCampo, setSavingCampo] = useState(false);

  // Un incidente "Resuelto"/"Cerrado" queda bloqueado para edición de sus
  // datos de contenido — solo se puede volver a abrir con "Reactivar".
  const estaBloqueado = estado === 'Resuelto' || estado === 'Cerrado';

  const [editando, setEditando] = useState(false);
  const [datosEdit, setDatosEdit] = useState<IncidenteDatosEditables>({
    titulo: incidente.titulo,
    descripcion: incidente.descripcion,
    fase: incidente.fase,
    solucionRelacionada: incidente.solucionRelacionada,
    checklistStepKey: incidente.checklistStepKey,
    ambiente: incidente.ambiente,
    asignadoA: incidente.asignadoA,
    sistema: incidente.sistema || '',
    canal: incidente.canal || '',
    categoriaSR: incidente.categoriaSR || ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [errorEdit, setErrorEdit] = useState<string | null>(null);

  const [detalleResolucion, setDetalleResolucion] = useState<DetalleResolucion>(
    incidente.detalleResolucion || { plataforma: PLATAFORMAS[0], queSeHizo: '', requirioAjusteTecnico: false }
  );
  const [savingResolucion, setSavingResolucion] = useState(false);

  const [detalleCierre, setDetalleCierre] = useState<DetalleCierre>(
    incidente.detalleCierre || { motivo: MOTIVOS_CIERRE[0], comentario: '' }
  );
  const [savingCierre, setSavingCierre] = useState(false);

  // Sección "Resolución" (ver CAMBIO 2/3): causa raíz siempre editable;
  // solución aplicada + vinculación a Errores Conocidos solo mientras el
  // incidente está Resuelto/Cerrado.
  const [causaRaiz, setCausaRaiz] = useState(incidente.causaRaiz || '');
  const [editandoCausaRaiz, setEditandoCausaRaiz] = useState(false);
  const [causaRaizInput, setCausaRaizInput] = useState(incidente.causaRaiz || '');
  const [savingCausaRaiz, setSavingCausaRaiz] = useState(false);

  const [solucionAplicada, setSolucionAplicada] = useState(incidente.solucionAplicada || '');
  const [solucionAplicadaInput, setSolucionAplicadaInput] = useState(incidente.solucionAplicada || '');
  const [savingSolucion, setSavingSolucion] = useState(false);
  const [advertenciaSolucion, setAdvertenciaSolucion] = useState(false);

  const [errorConocidoRef, setErrorConocidoRef] = useState<KnownErrorRef | null>(incidente.errorConocidoRef || null);
  const [notaSapManual, setNotaSapManual] = useState(incidente.notaSapManual || '');
  const [fechaResolucion, setFechaResolucion] = useState<any>(incidente.fechaResolucion);
  const [knownErrors, setKnownErrors] = useState<KnownError[]>([]);

  // Catálogo cargado una sola vez al abrir el modal — la búsqueda de
  // vinculación (IncidenteErrorConocidoLink) filtra 100% client-side sobre
  // esta lista, sin refetch por cada tecleo.
  useEffect(() => {
    getAllKnownErrors()
      .then(data => setKnownErrors(data.filter(isKnownErrorActivo)))
      .catch(err => console.error('Error cargando catálogo de errores conocidos:', err));
  }, []);

  const solucionLabel = SOLUCIONES[incidente.solucionRelacionada]?.label || incidente.solucionRelacionada;
  const asignado = miembros.find(m => m.userId === incidente.asignadoA);
  const fecha = incidente.fechaReporte?.toDate?.();
  const images = incidente.adjuntos.filter(a => a.contentType.startsWith('image/'));
  const documents = incidente.adjuntos.filter(a => !a.contentType.startsWith('image/'));
  const autorActual = user?.displayName || profile?.name || user?.email || 'Usuario';

  const handleEstadoChange = async (nuevo: IncidenteEstado) => {
    setEstado(nuevo);
    setSavingCampo(true);
    // Advertencia suave, no bloqueante: no impide guardar el cambio de
    // estado, solo recuerda documentar la solución si todavía no se hizo.
    setAdvertenciaSolucion(nuevo === 'Resuelto' && !solucionAplicada.trim());
    try {
      await updateIncidenteEstado(projectId, incidente.id, nuevo, fechaResolucion);
      if (nuevo === 'Resuelto' && !fechaResolucion) {
        // Optimistic: mismo patrón que handleAddComentario para la
        // bitácora, un objeto con `.toDate()` hasta que llegue el refetch.
        setFechaResolucion({ toDate: () => new Date() });
      }
      onUpdated();
    } catch (err) {
      console.error('Error actualizando estado:', err);
    } finally {
      setSavingCampo(false);
    }
  };

  const handlePrioridadChange = async (nueva: IncidentePrioridad) => {
    setPrioridad(nueva);
    setSavingCampo(true);
    try {
      await updateIncidentePrioridad(projectId, incidente.id, nueva);
      onUpdated();
    } catch (err) {
      console.error('Error actualizando prioridad:', err);
    } finally {
      setSavingCampo(false);
    }
  };

  const handleReactivar = async () => {
    setSavingCampo(true);
    try {
      await updateIncidenteEstado(projectId, incidente.id, 'Abierto');
      setEstado('Abierto');
      onUpdated();
    } catch (err) {
      console.error('Error reactivando incidente:', err);
    } finally {
      setSavingCampo(false);
    }
  };

  const handleAbrirEdicion = () => {
    setDatosEdit({
      titulo: incidente.titulo,
      descripcion: incidente.descripcion,
      fase: incidente.fase,
      solucionRelacionada: incidente.solucionRelacionada,
      checklistStepKey: incidente.checklistStepKey,
      ambiente: incidente.ambiente,
      asignadoA: incidente.asignadoA,
      sistema: incidente.sistema || '',
      canal: incidente.canal || '',
      categoriaSR: incidente.categoriaSR || ''
    });
    setErrorEdit(null);
    setEditando(true);
  };

  const handleGuardarEdicion = async () => {
    setErrorEdit(null);
    if (!datosEdit.titulo.trim()) { setErrorEdit('El título es obligatorio.'); return; }
    if (!datosEdit.descripcion.trim()) { setErrorEdit('La descripción es obligatoria.'); return; }

    setSavingEdit(true);
    try {
      await updateIncidenteDatos(projectId, incidente.id, datosEdit);
      setEditando(false);
      onUpdated();
    } catch (err: any) {
      setErrorEdit(err.message || 'Error al guardar los cambios.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleGuardarDetalleResolucion = async () => {
    setSavingResolucion(true);
    try {
      await updateDetalleResolucion(projectId, incidente.id, detalleResolucion);
      onUpdated();
    } catch (err) {
      console.error('Error guardando detalle de resolución:', err);
    } finally {
      setSavingResolucion(false);
    }
  };

  const handleGuardarDetalleCierre = async () => {
    setSavingCierre(true);
    try {
      await updateDetalleCierre(projectId, incidente.id, detalleCierre);
      onUpdated();
    } catch (err) {
      console.error('Error guardando motivo de cierre:', err);
    } finally {
      setSavingCierre(false);
    }
  };

  const handleAddComentario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comentario.trim()) return;
    setSavingComentario(true);
    try {
      await addBitacoraEntry(projectId, incidente.id, autorActual, comentario);
      setBitacora(prev => [...prev, { autor: autorActual, fecha: { toDate: () => new Date() }, texto: comentario.trim() }]);
      setComentario('');
      onUpdated();
    } catch (err) {
      console.error('Error agregando comentario:', err);
    } finally {
      setSavingComentario(false);
    }
  };

  const handleAbrirEdicionCausaRaiz = () => {
    setCausaRaizInput(causaRaiz);
    setEditandoCausaRaiz(true);
  };

  const handleGuardarCausaRaiz = async () => {
    setSavingCausaRaiz(true);
    try {
      await updateCausaRaiz(projectId, incidente.id, causaRaizInput);
      setCausaRaiz(causaRaizInput.trim());
      setEditandoCausaRaiz(false);
      onUpdated();
    } catch (err) {
      console.error('Error guardando causa raíz:', err);
    } finally {
      setSavingCausaRaiz(false);
    }
  };

  const handleGuardarSolucionAplicada = async () => {
    setSavingSolucion(true);
    try {
      await updateSolucionAplicada(projectId, incidente.id, solucionAplicadaInput);
      setSolucionAplicada(solucionAplicadaInput.trim());
      setAdvertenciaSolucion(false);
      onUpdated();
    } catch (err) {
      console.error('Error guardando solución aplicada:', err);
    } finally {
      setSavingSolucion(false);
    }
  };

  const handleLinkErrorConocido = async (ref: KnownErrorRef) => {
    await linkErrorConocido(projectId, incidente.id, ref);
    setErrorConocidoRef(ref);
    onUpdated();
  };

  const handleUnlinkErrorConocido = async () => {
    await unlinkErrorConocido(projectId, incidente.id);
    setErrorConocidoRef(null);
    onUpdated();
  };

  const handleGuardarNotaSapManual = async (value: string) => {
    await updateNotaSapManual(projectId, incidente.id, value);
    setNotaSapManual(value.trim());
    onUpdated();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))] z-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{incidente.fase}</span>
              <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{solucionLabel}</span>
              <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{incidente.ambiente}</span>
              {incidente.categoriaSR && (
                <span className="text-[10px] bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 px-2 py-0.5 rounded-full">SR · {incidente.categoriaSR}</span>
              )}
              {incidente.sistema && (
                <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{incidente.sistema}</span>
              )}
              {incidente.canal && (
                <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">
                  {CANALES_INTEGRACION.find(c => c.value === incidente.canal)?.label || incidente.canal}
                </span>
              )}
              {fecha && <span className="text-[10px] text-gray-500">{fecha.toLocaleString()}</span>}
            </div>
            <h3 className="text-lg font-medium text-white truncate">{incidente.titulo}</h3>
            {incidente.ticketOrigenId && (
              <Link to={`/proyecto/${projectId}/mesa-ayuda?ticket=${incidente.ticketOrigenId}`} className="text-[11px] text-blue-400 hover:text-blue-300">
                Viene del ticket {incidente.ticketOrigenNumero || incidente.ticketOrigenId} de la Mesa de ayuda
              </Link>
            )}
            {incidente.checklistStepKey && (
              <p className="text-[11px] text-gray-500 mt-1">
                Paso: <span className="text-gray-400">{getChecklistStepLabel(incidente.checklistStepKey)}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {!esCliente && !esAdminObservador && !estaBloqueado && !editando && (
              <button onClick={handleAbrirEdicion} className="text-gray-400 hover:text-white" title="Editar incidente">
                <Pencil className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Estado / Prioridad: editables normalmente; si el incidente está
              Resuelto/Cerrado quedan bloqueados en solo lectura y la única
              acción disponible es "Reactivar" (vuelve a "Abierto"). */}
          {estaBloqueado ? (
            <div className="flex items-center justify-between gap-3 flex-wrap bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${ESTADO_STYLES[estado]}`}>{estado}</span>
                <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${PRIORIDAD_STYLES[prioridad]}`}>{prioridad}</span>
                <span className="text-[11px] text-gray-500">Edición bloqueada mientras el incidente esté {estado.toLowerCase()}.</span>
              </div>
              {!esCliente && !esAdminObservador && (
                <button
                  onClick={handleReactivar}
                  disabled={savingCampo}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
                >
                  {savingCampo ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Reactivar incidente
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Estado</label>
                <select
                  value={estado}
                  onChange={(e) => handleEstadoChange(e.target.value as IncidenteEstado)}
                  disabled={savingCampo || esCliente || esAdminObservador}
                  className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-semibold border focus:outline-none ${ESTADO_STYLES[estado]}`}
                >
                  {ESTADOS.map(s => <option key={s} value={s} className="bg-zinc-800 text-white">{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Prioridad</label>
                <select
                  value={prioridad}
                  onChange={(e) => handlePrioridadChange(e.target.value as IncidentePrioridad)}
                  disabled={savingCampo || esCliente || esAdminObservador}
                  className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-semibold border focus:outline-none ${PRIORIDAD_STYLES[prioridad]}`}
                >
                  {PRIORIDADES.map(p => <option key={p} value={p} className="bg-zinc-800 text-white">{p}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Edición de datos de contenido (título, descripción, fase,
              solución/paso, ambiente, asignado) — reemplaza temporalmente
              los bloques de solo lectura de más abajo mientras editando=true. */}
          {editando && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Editar incidente</h4>
              {errorEdit && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{errorEdit}</div>
              )}

              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Título</label>
                <input
                  type="text"
                  value={datosEdit.titulo}
                  onChange={(e) => setDatosEdit(prev => ({ ...prev, titulo: e.target.value }))}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Descripción</label>
                <textarea
                  value={datosEdit.descripcion}
                  onChange={(e) => setDatosEdit(prev => ({ ...prev, descripcion: e.target.value }))}
                  rows={4}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Fase</label>
                  <select
                    value={datosEdit.fase}
                    onChange={(e) => setDatosEdit(prev => ({ ...prev, fase: e.target.value as IncidenteFase }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    {FASES.map(f => <option key={f} value={f}>{FASE_LABELS[f]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">{modoArea ? 'Área' : 'Solución relacionada'}</label>
                  <select
                    value={datosEdit.solucionRelacionada}
                    onChange={(e) => setDatosEdit(prev => ({ ...prev, solucionRelacionada: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    {modoArea && <option value="">Sin área</option>}
                    {solucionesActivas.map(slug => (
                      <option key={slug} value={slug}>{SOLUCIONES[slug]?.label || slug}</option>
                    ))}
                  </select>
                </div>
              </div>

              <ChecklistStepPicker
                fase={datosEdit.fase}
                solucionRelacionada={datosEdit.solucionRelacionada}
                checklistStepKey={datosEdit.checklistStepKey}
                onChange={(checklistStepKey) => setDatosEdit(prev => ({ ...prev, checklistStepKey }))}
              />

              {incidente.categoriaSR && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Categoría de SR</label>
                    <select
                      value={datosEdit.categoriaSR || ''}
                      onChange={(e) => setDatosEdit(prev => ({ ...prev, categoriaSR: e.target.value as IncidenteCategoriaSR }))}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                    >
                      {CATEGORIAS_SR.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Sistema</label>
                    <select
                      value={datosEdit.sistema || ''}
                      onChange={(e) => setDatosEdit(prev => ({ ...prev, sistema: e.target.value as IncidentePlataforma }))}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Sin especificar</option>
                      {PLATAFORMAS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Canal</label>
                    <select
                      value={datosEdit.canal || ''}
                      onChange={(e) => setDatosEdit(prev => ({ ...prev, canal: e.target.value as IncidenteCanal }))}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Sin canal específico</option>
                      {CANALES_INTEGRACION.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Ambiente</label>
                  <select
                    value={datosEdit.ambiente}
                    onChange={(e) => setDatosEdit(prev => ({ ...prev, ambiente: e.target.value as IncidenteAmbiente }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    {AMBIENTES.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Asignado a</label>
                  <select
                    value={datosEdit.asignadoA}
                    onChange={(e) => setDatosEdit(prev => ({ ...prev, asignadoA: e.target.value }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Sin asignar</option>
                    {miembros.map(m => (
                      <option key={m.userId} value={m.userId}>{m.email} ({m.rol})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setEditando(false)}
                  className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGuardarEdicion}
                  disabled={savingEdit}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
                >
                  {savingEdit ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Guardar cambios
                </button>
              </div>
            </div>
          )}

          {!editando && (
            <>
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Descripción</h4>
                <p className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{incidente.descripcion}</p>
              </div>

              {/* Causa raíz: a diferencia de la descripción ("qué pasó al
                  reportar"), esto es "por qué pasó" — se puede completar
                  en cualquier momento, no depende del estado. */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Causa raíz</h4>
                  {!esCliente && !esAdminObservador && !editandoCausaRaiz && (
                    <button onClick={handleAbrirEdicionCausaRaiz} className="text-gray-400 hover:text-white" title="Editar causa raíz">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {editandoCausaRaiz ? (
                  <div className="space-y-2">
                    <textarea
                      value={causaRaizInput}
                      onChange={(e) => setCausaRaizInput(e.target.value)}
                      rows={3}
                      placeholder="¿Por qué ocurrió el error? Se puede completar después de analizar el incidente."
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditandoCausaRaiz(false)}
                        className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleGuardarCausaRaiz}
                        disabled={savingCausaRaiz}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
                      >
                        {savingCausaRaiz ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Guardar
                      </button>
                    </div>
                  </div>
                ) : causaRaiz ? (
                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{causaRaiz}</p>
                ) : (
                  <p className="text-xs text-gray-500">Sin documentar todavía.</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Reportado por</h4>
                  <p className="text-gray-300">{incidente.reportadoPorNombre}</p>
                  <p className="text-gray-500 text-xs">{incidente.reportadoPorEmail}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Asignado a</h4>
                  <p className="text-gray-300">{asignado ? asignado.email : 'Sin asignar'}</p>
                </div>
              </div>
            </>
          )}

          {/* Resolución: sección nueva, siempre visible pero con contenido
              según estado — separada de "Detalle de Resolución" (técnico:
              plataforma/ajuste) y "Motivo de Cierre" (por qué se cerró),
              que son bloques propios más abajo con su propio encabezado. */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Resolución</h4>
            {estado !== 'Resuelto' && estado !== 'Cerrado' ? (
              <p className="text-xs text-gray-500">La información de resolución se completará al cerrar el incidente.</p>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Solución aplicada</label>
                  {!esCliente && !esAdminObservador ? (
                    <>
                      <textarea
                        value={solucionAplicadaInput}
                        onChange={(e) => setSolucionAplicadaInput(e.target.value)}
                        rows={3}
                        placeholder="¿Qué se hizo para resolverlo?"
                        className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
                      />
                      {advertenciaSolucion && (
                        <p className="text-[11px] text-amber-400 flex items-center gap-1.5 mt-1.5">
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                          Considera documentar la solución aplicada para referencia futura.
                        </p>
                      )}
                      {solucionAplicadaInput !== solucionAplicada && (
                        <button
                          type="button"
                          onClick={handleGuardarSolucionAplicada}
                          disabled={savingSolucion}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all mt-2"
                        >
                          {savingSolucion ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Guardar
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{solucionAplicada || 'Sin documentar todavía.'}</p>
                  )}
                </div>

                <IncidenteErrorConocidoLink
                  knownErrors={knownErrors}
                  errorConocidoRef={errorConocidoRef}
                  notaSapManual={notaSapManual}
                  disabled={esCliente || esAdminObservador}
                  onLink={handleLinkErrorConocido}
                  onUnlink={handleUnlinkErrorConocido}
                  onSaveNotaSapManual={handleGuardarNotaSapManual}
                />

                {fechaResolucion?.toDate && (
                  <p className="text-xs text-gray-500 flex items-center gap-1.5">
                    <CalendarCheck className="w-3.5 h-3.5" />
                    Resuelto el {formatFechaConTiempoRelativo(fechaResolucion.toDate())}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Detalle de Resolución: editable mientras el estado sea
              "Resuelto"; si ya no lo es pero quedó guardado de antes, se
              muestra de solo lectura para que no se pierda de vista. */}
          {estado === 'Resuelto' && !esCliente && !esAdminObservador ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Detalle de Resolución</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Plataforma</label>
                  <select
                    value={detalleResolucion.plataforma}
                    onChange={(e) => setDetalleResolucion(prev => ({ ...prev, plataforma: e.target.value as any }))}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    {PLATAFORMAS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 mt-5 text-xs text-gray-300">
                  <input
                    type="checkbox"
                    checked={detalleResolucion.requirioAjusteTecnico}
                    onChange={(e) => setDetalleResolucion(prev => ({ ...prev, requirioAjusteTecnico: e.target.checked }))}
                    className="accent-blue-500 w-4 h-4"
                  />
                  Requirió ajuste técnico
                </label>
              </div>

              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Qué se hizo</label>
                <textarea
                  value={detalleResolucion.queSeHizo}
                  onChange={(e) => setDetalleResolucion(prev => ({ ...prev, queSeHizo: e.target.value }))}
                  rows={3}
                  placeholder="Describe la solución aplicada (opcional)..."
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>

              {detalleResolucion.requirioAjusteTecnico && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Tipo de ajuste</label>
                    <select
                      value={detalleResolucion.tipoAjuste || ''}
                      onChange={(e) => setDetalleResolucion(prev => ({ ...prev, tipoAjuste: (e.target.value || undefined) as any }))}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Sin especificar</option>
                      {TIPOS_AJUSTE.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-1">Programa / Transacción</label>
                    <input
                      type="text"
                      value={detalleResolucion.programaOTransaccion || ''}
                      onChange={(e) => setDetalleResolucion(prev => ({ ...prev, programaOTransaccion: e.target.value }))}
                      placeholder="Ej: ZME21N"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleGuardarDetalleResolucion}
                disabled={savingResolucion}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
              >
                {savingResolucion ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar detalle
              </button>
            </div>
          ) : incidente.detalleResolucion ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-1.5">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Detalle de Resolución</h4>
              <p className="text-xs text-gray-500">Plataforma: <span className="text-gray-300">{incidente.detalleResolucion.plataforma}</span></p>
              {incidente.detalleResolucion.queSeHizo && (
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{incidente.detalleResolucion.queSeHizo}</p>
              )}
              {incidente.detalleResolucion.requirioAjusteTecnico && (
                <p className="text-xs text-gray-500">
                  Ajuste técnico: <span className="text-gray-300">{incidente.detalleResolucion.tipoAjuste || 'Sin especificar'}</span>
                  {incidente.detalleResolucion.programaOTransaccion && ` — ${incidente.detalleResolucion.programaOTransaccion}`}
                </p>
              )}
            </div>
          ) : null}

          {/* Motivo de Cierre: mismo patrón editable/solo-lectura. */}
          {estado === 'Cerrado' && !esCliente && !esAdminObservador ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Motivo de Cierre</h4>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Motivo</label>
                <select
                  value={detalleCierre.motivo}
                  onChange={(e) => setDetalleCierre(prev => ({ ...prev, motivo: e.target.value as any }))}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500"
                >
                  {MOTIVOS_CIERRE.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] text-gray-500 mb-1">Comentario</label>
                <textarea
                  value={detalleCierre.comentario || ''}
                  onChange={(e) => setDetalleCierre(prev => ({ ...prev, comentario: e.target.value }))}
                  rows={2}
                  placeholder="Comentario adicional (opcional)..."
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500 resize-none"
                />
              </div>
              <button
                onClick={handleGuardarDetalleCierre}
                disabled={savingCierre}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#fff] transition-all"
              >
                {savingCierre ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar detalle
              </button>
            </div>
          ) : incidente.detalleCierre ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-1.5">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Motivo de Cierre</h4>
              <p className="text-xs text-gray-500">Motivo: <span className="text-gray-300">{incidente.detalleCierre.motivo}</span></p>
              {incidente.detalleCierre.comentario && (
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{incidente.detalleCierre.comentario}</p>
              )}
            </div>
          ) : null}

          {images.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Capturas</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {images.map((img) => (
                  <a key={img.url} href={img.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={img.url}
                      alt={img.name}
                      className="w-full rounded-xl border border-white/10 hover:border-blue-400 transition-all"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {documents.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Adjuntos</h4>
              <div className="space-y-2">
                {documents.map((docFile) => (
                  <a
                    key={docFile.url}
                    href={docFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3 hover:border-blue-400 transition-all text-sm text-gray-300"
                  >
                    <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{docFile.name}</span>
                    <Download className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Bitácora */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Bitácora</h4>
            <div className="space-y-3 mb-3">
              {bitacora.length === 0 ? (
                <p className="text-xs text-gray-500">Todavía no hay comentarios.</p>
              ) : (
                bitacora.map((entry, idx) => (
                  <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-300">{entry.autor}</span>
                      <span className="text-[10px] text-gray-500">
                        {entry.fecha?.toDate?.()?.toLocaleString?.() || ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.texto}</p>
                  </div>
                ))
              )}
            </div>

            {!esCliente && !esAdminObservador && (
              <form onSubmit={handleAddComentario} className="flex gap-2">
                <input
                  type="text"
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Agregar un comentario..."
                  className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                />
                <button
                  type="submit"
                  disabled={savingComentario || !comentario.trim()}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
                >
                  {savingComentario ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
